/**
 * useOrderSearch Hook
 *
 * CRITICAL FIX: Updated to match backend API parameter expectations
 * Backend only supports: page, size, sortBy, sortDir, buyerId, status (single), orderNumber, fromDate, toDate
 */

import { useState, useEffect, useCallback } from 'react';
import type {
  OrderFilters,
  PaginationState,
  OrderSearchRequest,
  OrderSearchResponse,
  OrderSearchResultItem,
} from '../types/filter.types';

const COMMERCE_SERVICE_URL =
  import.meta.env.VITE_COMMERCE_SERVICE_URL ||
  'http://localhost:8083/commerce-service';

function getAuthToken(): string | null {
  return localStorage.getItem('saubhagya_jwt_token');
}

function buildSearchRequest(
  filters: OrderFilters,
  pagination: PaginationState,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc'
): OrderSearchRequest {
  // Debug: Log filters being received
  console.log('[buildSearchRequest] filters received:', filters);
  console.log('[buildSearchRequest] filters.searchTerm:', filters.searchTerm, 'Type:', typeof filters.searchTerm);

  const request: OrderSearchRequest = {
    page: pagination.page - 1,
    pageSize: pagination.pageSize,
  };

  if (filters.searchTerm) {
    request.searchTerm = filters.searchTerm;
    console.log('[buildSearchRequest] Added searchTerm to request:', filters.searchTerm);
  } else {
    console.log('[buildSearchRequest] No searchTerm to add, filters.searchTerm is:', filters.searchTerm);
  }
  if (filters.statuses.length > 0) {
    request.statuses = filters.statuses;
  }
  if (filters.orderDateRange.from) {
    request.orderDateFrom = filters.orderDateRange.from;
  }
  if (filters.orderDateRange.to) {
    request.orderDateTo = filters.orderDateRange.to;
  }
  if (filters.expectedDeliveryDateRange.from) {
    request.expectedDeliveryFrom = filters.expectedDeliveryDateRange.from;
  }
  if (filters.expectedDeliveryDateRange.to) {
    request.expectedDeliveryTo = filters.expectedDeliveryDateRange.to;
  }
  if (filters.actualDeliveryDateRange.from) {
    request.actualDeliveryFrom = filters.actualDeliveryDateRange.from;
  }
  if (filters.actualDeliveryDateRange.to) {
    request.actualDeliveryTo = filters.actualDeliveryDateRange.to;
  }
  if (filters.amountRange.min !== null) {
    request.minAmount = filters.amountRange.min;
  }
  if (filters.amountRange.max !== null) {
    request.maxAmount = filters.amountRange.max;
  }
  if (filters.customerType !== 'ALL') {
    request.customerType = filters.customerType;
  }
  if (filters.location.state) {
    request.state = filters.location.state;
  }
  if (filters.location.city) {
    request.city = filters.location.city;
  }

  // Add sorting parameters
  if (sortBy) {
    request.sortBy = sortBy;
  }
  if (sortDirection) {
    request.sortDirection = sortDirection;
  }

  return request;
}

async function searchOrders(
  request: OrderSearchRequest
): Promise<OrderSearchResponse> {
  const token = getAuthToken();

  // CRITICAL: Log token details for debugging
  console.log('[useOrderSearch] Token retrieval check:', {
    hasToken: !!token,
    tokenLength: token ? token.length : 0,
    tokenStart: token ? token.substring(0, 30) : 'NO TOKEN',
    tokenEnd: token ? token.substring(token.length - 20) : 'N/A'
  });

  const queryParams = new URLSearchParams();

  // CRITICAL FIX: Map frontend parameters to backend-expected parameters
  // Backend expects: page, size, sortBy, sortDir, buyerId, status, orderNumber, fromDate, toDate
  // See: SalesOrderController.java lines 194-232

  // Debug: Log the entire request object
  console.log('[useOrderSearch] searchOrders called with request:', request);

  // Pagination (backend expects 'size' not 'pageSize')
  queryParams.append('page', (request.page).toString());
  queryParams.append('size', request.pageSize.toString());

  // Search Term → searchTerm (multi-field search: order number, customer name, email, phone, address)
  if (request.searchTerm) {
    queryParams.append('searchTerm', request.searchTerm);
    console.log('[useOrderSearch] Sending searchTerm for multi-field search:', request.searchTerm);
  } else {
    console.log('[useOrderSearch] No searchTerm found in request');
  }

  // Status Filter (backend only supports SINGLE status, not array)
  // TODO: Backend enhancement needed to support multiple statuses
  if (request.statuses && request.statuses.length > 0) {
    queryParams.append('status', request.statuses[0]);
    console.log('[useOrderSearch] Using first status (backend limitation):', request.statuses[0]);
    if (request.statuses.length > 1) {
      console.warn('[useOrderSearch] Backend only supports single status. Ignoring:', request.statuses.slice(1));
    }
  }

  // Order Date Range → fromDate/toDate (backend expects these names)
  if (request.orderDateFrom) {
    queryParams.append('fromDate', request.orderDateFrom);
  }
  if (request.orderDateTo) {
    queryParams.append('toDate', request.orderDateTo);
  }

  // LOCATION FILTERS (NOW SUPPORTED - added to query params)
  if (request.city) {
    queryParams.append('city', request.city);
    console.log('[useOrderSearch] Added city filter:', request.city);
  }
  if (request.state) {
    queryParams.append('state', request.state);
    console.log('[useOrderSearch] Added state filter:', request.state);
  }

  // Amount range filtering
  if (request.minAmount !== undefined && request.minAmount !== null) {
    queryParams.append('minAmount', request.minAmount.toString());
    console.log('[useOrderSearch] Added minAmount filter:', request.minAmount);
  }
  if (request.maxAmount !== undefined && request.maxAmount !== null) {
    queryParams.append('maxAmount', request.maxAmount.toString());
    console.log('[useOrderSearch] Added maxAmount filter:', request.maxAmount);
  }

  // UNSUPPORTED FILTERS (backend does not support these yet)
  // Log warnings for tracking what's being ignored
  if (request.expectedDeliveryFrom || request.expectedDeliveryTo) {
    console.warn('[useOrderSearch] Expected delivery date filtering NOT supported by backend yet');
  }
  if (request.actualDeliveryFrom || request.actualDeliveryTo) {
    console.warn('[useOrderSearch] Actual delivery date filtering NOT supported by backend yet');
  }
  if (request.customerType && request.customerType !== 'ALL') {
    console.warn('[useOrderSearch] Customer type filtering NOT supported by backend yet');
  }

  // Sales unit filtering
  if (request.salesUnitIds && request.salesUnitIds.length > 0) {
    queryParams.append('salesUnitIds', request.salesUnitIds.join(','));
    console.log('[useOrderSearch] Added salesUnitIds filter:', request.salesUnitIds);
  }

  // Sorting (add default sorting)
  if (request.sortBy) {
    queryParams.append('sortBy', request.sortBy);
  } else {
    queryParams.append('sortBy', 'createdAt');
  }

  if (request.sortDirection) {
    queryParams.append('sortDir', request.sortDirection);
  } else {
    queryParams.append('sortDir', 'desc');
  }

  const apiUrl = COMMERCE_SERVICE_URL + '/api/v1/orders?' + queryParams.toString();
  console.log('[useOrderSearch] Fetching orders from:', apiUrl);

  const response = await fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: 'Bearer ' + token }),
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to search orders');
  }

  const apiResponse = await response.json();

  // Map backend response to expected format
  // Backend returns: { success, message, data: { content, page, size, totalElements, totalPages } }
  const backendData = apiResponse.data || apiResponse;

  return {
    orders: (backendData.content || []).map((order: any) => ({
      id: order.id,
      orderNumber: order.orderNumber,
      orderDate: order.orderDate,
      orderStatus: order.status,
      customerName: order.buyerName,
      customerEmail: order.contactEmail || "",
      customerType: "B2B" as const,
      totalAmount: order.totalAmount,
      balanceDue: order.outstandingAmount ?? 0,
      expectedDeliveryDate: order.requiredDeliveryDate,
      actualDeliveryDate: order.actualDeliveryDate,
      city: (order.deliveryAddress?.split(",")[order.deliveryAddress.split(",").length - 2]?.trim?.() || ""),
      state: (order.deliveryAddress?.split(",")[order.deliveryAddress.split(",").length - 1]?.split("-")[0]?.trim?.() || "")
    })),
    totalElements: backendData.totalElements || 0,
    totalPages: backendData.totalPages || 0,
    currentPage: backendData.page || 0,
    pageSize: backendData.size || 20,
    hasNext: !backendData.last && !backendData.empty,
    hasPrevious: !backendData.first && !backendData.empty,
  };
}

export interface UseOrderSearchReturn {
  orders: OrderSearchResultItem[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useOrderSearch(
  filters: OrderFilters,
  pagination: PaginationState,
  sortBy: string = 'createdAt',
  sortDirection: 'asc' | 'desc' = 'desc',
  salesUnitIds?: number[]
): UseOrderSearchReturn {
  const [orders, setOrders] = useState<OrderSearchResultItem[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const request = buildSearchRequest(filters, pagination, sortBy, sortDirection);
      if (salesUnitIds && salesUnitIds.length > 0) {
        request.salesUnitIds = salesUnitIds;
      }
      const response = await searchOrders(request);

      setOrders(response.orders);
      setTotalElements(response.totalElements);
      setTotalPages(response.totalPages);
      setCurrentPage(response.currentPage + 1);
      setPageSize(response.pageSize);
      setHasNext(response.hasNext);
      setHasPrevious(response.hasPrevious);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch orders';
      setError(errorMessage);
      console.error('[useOrderSearch] Error fetching orders:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters, pagination, sortBy, sortDirection, salesUnitIds]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return {
    orders,
    totalElements,
    totalPages,
    currentPage,
    pageSize,
    hasNext,
    hasPrevious,
    isLoading,
    error,
    refetch: fetchOrders,
  };
}
